import type { UpgradeReason } from "@sill/shared/components/billing/pricing-copy";

import { ApiError } from "../../lib/api";

// Maps a request URL to the contextual UpgradeReason used to key copy in the
// shared UPGRADE_COPY table. Unknown URLs fall back to "pricing_page".
// SILL-539 removed the /api/plaid/* → "plaid_link" row: Plaid carries no
// feature gate, so those endpoints can no longer emit feature_not_available.
const URL_TO_REASON: { pattern: RegExp; reason: UpgradeReason }[] = [
  { pattern: /\/api\/rate-alerts/, reason: "custom_alert" },
];

type Listener = (reason: UpgradeReason) => void;
const listeners = new Set<Listener>();

// Subscribe to paywall-triggered events. Returns an unsubscribe function.
export function onPaywallTriggered(cb: Listener): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

// Inspect an error thrown by apiRequest. If it's a 403 with structured body
// `code=feature_not_available` and `feature=sill_plus`, notify all listeners
// with a reason inferred from the request URL. Returns true if the error was
// recognised as a paywall trigger (regardless of whether any listeners were
// registered). Non-ApiError or unrelated 403s (e.g. policy.Check denials)
// are ignored.
export function inspectErrorForPaywall(
  err: unknown,
  requestUrl: string,
): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.status !== 403) return false;
  if (err.structured?.code !== "feature_not_available") return false;
  if (err.structured?.feature !== "sill_plus") return false;
  const reason =
    URL_TO_REASON.find((m) => m.pattern.test(requestUrl))?.reason ??
    "pricing_page";
  listeners.forEach((l) => l(reason));
  return true;
}

// Intent-driven paywall trigger. Use when the caller knows BEFORE issuing
// a request that the user is non-Plus. Skipping the round-trip + 403 cycle
// is both faster AND keeps the backend's audit log clean of "user clicked
// thing they couldn't do" noise.
//
// Same event bus, same subscribers — GlobalUpgradeSheet handles either
// source identically.
export function triggerPaywall(reason: UpgradeReason): void {
  listeners.forEach((l) => l(reason));
}

// Test-only helper for clearing the module-level listener set between cases.
// Has no production callers.
export function __resetPaywallInterceptor() {
  listeners.clear();
}
